import { IApi } from '@/setup/api-client'
import { useApiQuery } from '@/utils/api/api-query'
import { useApiToolbox } from '@/utils/api/use-api-toolbox'
import type { IAgentDetailResponse } from '@/features/agents/types/agent-types'

export const AGENT_QUERIES_BASE_KEY = ['agents']

export interface IGetAgentQueryParams {
    organizationId: string | null
    brandSlug: string
    agentUuid: string
}

export class GetAgentQuery {
    static getKey(params: IGetAgentQueryParams) {
        return [...AGENT_QUERIES_BASE_KEY, params.organizationId, params.brandSlug, 'get-agent', params.agentUuid]
    }

    static async call(api: IApi, params: IGetAgentQueryParams): Promise<IAgentDetailResponse | undefined> {
        return api
            .get<IAgentDetailResponse>(
                `/organization/${params.organizationId}/brands/${params.brandSlug}/agents/${params.agentUuid}`
            )
            .then((res) => res?.data)
    }
}

export function useGetAgentQuery(
    agentUuid: string | null,
    options?: { enabled?: boolean; refetchInterval?: number | false }
) {
    const { api, apiParams } = useApiToolbox()
    const organizationId = apiParams.organizationId ?? ''

    const isEnabled = !!organizationId && !!agentUuid && !!apiParams.brandSlug && (options?.enabled ?? true)

    // When polling, match staleTime to the interval so re-renders don't trigger extra fetches.
    // Otherwise default to 30s. Previously this was `undefined`, which overrode the global
    // 10-min default and collapsed staleTime to 0 — making the query stale-on-arrival and
    // refetching on every mount (it's mounted once per chat message) and every window focus.
    // 30s keeps the intentional cross-tab focus sync below while bounding the refetch rate.
    const staleTime = typeof options?.refetchInterval === 'number' ? options.refetchInterval : 30 * 1000

    const queryKey = GetAgentQuery.getKey({
        organizationId: organizationId || null,
        brandSlug: apiParams.brandSlug,
        agentUuid: agentUuid ?? '',
    })

    return useApiQuery({
        queryKey,
        queryFn: () => {
            return GetAgentQuery.call(api, {
                organizationId,
                brandSlug: apiParams.brandSlug,
                agentUuid: agentUuid!,
            })
        },
        enabled: isEnabled,
        refetchInterval: options?.refetchInterval,
        staleTime,
        // Refetch on tab focus to sync cross-tab state (e.g., message sent in another tab)
        refetchOnWindowFocus: true,
    })
}
